import { useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { ShieldCheckIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/solid';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

export default function AuthStatus() {
  if (process.env.NEXT_PUBLIC_E2E_TEST === '1') {
    return null;
  }

  const { currentUser, refreshToken, logOut } = useAuth();
  const [status, setStatus] = useState<'ok' | 'refreshing' | 'error'>('refreshing');
  const [isVisible, setIsVisible] = useState(!!currentUser);
  
  // Run authentication check on initial load and when auth state changes
  useEffect(() => {
    // Set initial state based on user presence
    if (currentUser) {
      setStatus('refreshing');
      setIsVisible(true);
      
      // Check token validity when component mounts or user changes
      const checkAuth = async () => {
        try {
          console.log('Checking auth token validity...');
          const token = await refreshToken();
          
          if (token) {
            console.log('Auth token valid');
            setStatus('ok');
            // Keep visible for a few seconds when token is valid
            setTimeout(() => setIsVisible(false), 3000);
          } else {
            console.error('Auth token invalid');
            setStatus('error');
            setIsVisible(true);
          }
        } catch (err) {
          console.error('Auth check failed:', err);
          setStatus('error');
          setIsVisible(true);
        }
      };
      
      // Run auth check after a short delay to allow Firebase Auth to initialize
      setTimeout(checkAuth, 500);
    } else {
      setIsVisible(false);
    }
  }, [currentUser, refreshToken]);
   // Show indicator for errors or during auth operations
  useEffect(() => {
    // Always show errors
    if (status === 'error') {
      setIsVisible(true);
    }
    
    // Show refreshing status for a short time
    if (status === 'refreshing') {
      setIsVisible(true);
      
      // Set a timeout to hide the indicator if it gets stuck in 'refreshing'
      const timer = setTimeout(() => {
        setStatus('error');
      }, 10000); // If refreshing takes more than 10 seconds, show error
      
      return () => clearTimeout(timer);
    }
  }, [status]);
  
  if (!isVisible) return null;
  
  // Enhanced UI with more noticeable styling
  return (
    <div className={`fixed top-4 md:top-4 right-4 px-4 py-2 rounded-md flex items-center gap-2 text-sm shadow-md transition-all duration-300 z-50 ${
      status === 'ok' ? 'bg-green-100 text-green-700 border border-green-300' :
      status === 'refreshing' ? 'bg-blue-100 text-blue-700 border border-blue-300' : 
      'bg-red-100 text-red-700 border border-red-300'
    }`}>
      {status === 'ok' ? (
        <>
          <ShieldCheckIcon className="w-4 h-4" />
          <span>Session active</span>
        </>
      ) : status === 'refreshing' ? (
        <>
          <div className="w-3 h-3 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span>Refreshing...</span>
        </>
      ) : (
        <>
          <ExclamationTriangleIcon className="w-4 h-4" />
          <span>Authentication error</span>
          <button 
            onClick={() => window.location.reload()} 
            className="ml-1 px-2 py-1 bg-red-200 hover:bg-red-300 rounded text-red-800"
          >
            Refresh
          </button>
          <button
            onClick={async () => {
              try {
                await logOut();
                window.location.href = '/';
              } catch (err) {
                console.error("Error signing out:", err);
              }
            }}
            className="ml-1 px-2 py-1 bg-muted hover:bg-gray-300 rounded text-foreground flex items-center"
          >
            <ArrowRightOnRectangleIcon className="w-3 h-3 mr-1" />
            Sign Out
          </button>
        </>
      )}
    </div>
  );
}
